import { ChangeEvent, SetStateAction, useEffect, useState } from 'react';

import {
    Box,
    InputAdornment,
    InputBase,
    InputBaseProps,
    Stack,
    styled,
    Typography,
} from '@mui/material';
import { useField, useFormikContext } from 'formik';
import { NumericFormat } from 'react-number-format';

import { useCurrency } from 'src/hooks/useCurrency';

interface InputBaseFieldProps extends InputBaseProps {
    label?: string;
    currency?: string;
    adornmentPos?: 'start' | 'end';
    percentage?: boolean;
    dataTestId?: string;
    min?: number;
    max?: number;
    showMessage?: boolean;
    rangeLimit?: { lowest: number; highest: number };
    disabled?: boolean;
}

const InputField = styled(InputBase)(({ error, disabled }) => {
    return {
        border: 'thin solid #f3a256 !important',
        borderRadius: '8px',
        padding: '10px',
        marginTop: '5px',
        minWidth: '100px',
        '&:hover': {
            border: '2px solid #f3a256 !important',
        },
        ...(error && {
            border: '2px solid #f06c62 !important',
            '&:hover': {
                border: '2px solid #f06c62 !important',
            },
        }),
        ...(disabled && {
            backgroundColor: '#fafafa',
            border: 'thin solid #ccc',
            '&:hover': {
                border: 'thin solid #ccc',
            },
        }),
    };
});

const rangeLimiter = (
    value: number,
    rangeLimit: { lowest: number; highest: number },
    showError: (message: string, error: boolean) => void
) => {
    const { lowest, highest } = rangeLimit;

    return value < lowest
        ? (showError('Too Low', true), lowest)
        : value > highest
          ? (showError('Too High', true), highest)
          : null;
};

const InputBaseField = ({
    label,
    placeholder,
    name,
    disabled,
    type,
    currency,
    adornmentPos,
    percentage,
    dataTestId = `${name}-input`,
    min,
    max,
    showMessage = true,
    rangeLimit,
    ...props
}: InputBaseFieldProps) => {
    const { currencySymbol } = useCurrency();
    const { errors, setFieldError, touched } = useFormikContext();
    const [field, meta] = useField({ name, type });
    const [inputValue, setInputValue] = useState(props.value ?? field.value);
    const [hasError, setHasError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        const inputFieldName = name.split('.')[1];
        const error = errors[name] || meta.error;
        const isTouched = touched[name] || meta.touched;

        if (!isTouched) return;

        const showError = (
            message: SetStateAction<string>,
            errorStatus: boolean | ((prevState: boolean) => boolean)
        ) => {
            setHasError(errorStatus);
            setErrorMessage(message);
        };

        if (error) {
            showError(error, true);
        } else {
            showError('', false);
        }

        if (type === 'number' && (inputFieldName === 'sales' || inputFieldName === 'commission')) {
            if (!inputValue) {
                showError('Required', true);
            } else {
                showError('', false);
                if (inputFieldName === 'sales' && rangeLimit)
                    rangeLimiter(inputValue, rangeLimit, showError);
            }
        }
    }, [
        meta.touched,
        errors,
        rangeLimit,
        type,
        name,
        touched,
        inputValue,
        meta.error,
        field.name,
        field.value,
    ]);

    // #region Handlers and Validators
    const validate = (value: number) => {
        if (min > max) [min, max] = [max, min];
        return value < min || value > max ? `Value must be between ${min} and ${max}` : undefined;
    };

    const handleValueChange = (
        e: ChangeEvent<HTMLInputElement>,
        setValue: (value: number) => void,
        min: number,
        max: number,
        name: string
    ) => {
        e.preventDefault();

        const { value } = e.target;
        const numericValue = Number(value);

        setValue(numericValue);

        const error = validate(numericValue);
        setFieldError(name, error);
    };

    const handleCurrencyChange = (e: ChangeEvent<HTMLInputElement>) => {
        return handleValueChange(e, setInputValue, min, max, name);
    };

    const handlePercentageChange = (e: ChangeEvent<HTMLInputElement>) => {
        return handleValueChange(e, setInputValue, min, max, name);
    };

    const handleBlurCurrency = () => {
        const error = validate(inputValue);
        setFieldError(name, error);
    };

    // #region Renderers
    const renderNumericFormat = (isCurrency: boolean) => {
        return (
            <NumericFormat
                {...props}
                defaultValue={0.0}
                name={field.name}
                value={props.value ?? field.value}
                onChange={isCurrency ? handleCurrencyChange : handlePercentageChange}
                onBlur={isCurrency ? handleBlurCurrency : field.onBlur}
                decimalSeparator="."
                decimalScale={2}
                inputProps={{
                    'data-testid': dataTestId,
                }}
                fixedDecimalScale
                thousandSeparator
                customInput={InputBase}
                placeholder={placeholder}
                autoComplete="off"
                className={`form-input ${meta.touched && meta.error ? 'input-error' : ''}`}
                disabled={disabled}
                endAdornment={
                    adornmentPos === 'end' && (
                        <InputAdornment position="start">
                            {isCurrency ? (currency ?? currencySymbol) : '%'}
                        </InputAdornment>
                    )
                }
                startAdornment={
                    adornmentPos === 'start' && (
                        <InputAdornment position="start">
                            {isCurrency ? (currency ?? currencySymbol) : '%'}
                        </InputAdornment>
                    )
                }
            />
        );
    };

    const renderError = (value: number) => {
        return (
            !disabled &&
            showMessage &&
            (meta.touched || meta.error) && (
                <label
                    className="input-base-field-error"
                    style={{
                        wordWrap: 'break-word',
                        maxWidth: '100%',
                        display: 'flex',
                        position: 'relative',
                        bottom: 0,
                    }}
                >
                    {meta.error || validate(value)}
                </label>
            )
        );
    };

    if (currency) {
        return (
            <Box display="flex" flexDirection="column">
                {renderNumericFormat(true)}
                {renderError(inputValue)}
            </Box>
        );
    }

    if (percentage) {
        return (
            <Box display="flex" flexDirection="column">
                {renderNumericFormat(false)}
                {renderError(inputValue)}
            </Box>
        );
    }

    return (
        <>
            <Stack
                sx={{
                    position: 'relative',
                    width: '100%',
                }}
            >
                {label && <label className="mb-5 font-bold">{label}</label>}
                <InputField
                    {...field}
                    {...props}
                    value={props.value ?? field.value}
                    inputProps={{
                        'data-testid': dataTestId,
                    }}
                    placeholder={placeholder || null}
                    autoComplete="off"
                    disabled={disabled}
                    type={type ?? 'text'}
                    error={hasError}
                    onChange={(e) => {
                        setInputValue(Number(e.target.value));
                        field.onChange(e);
                    }}
                />
                {hasError && (
                    <Typography
                        sx={{
                            color: '#f06c62 !important',
                            fontSize: '12px !important',

                            mt: '4px',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                        }}
                    >
                        {errorMessage}
                    </Typography>
                )}
            </Stack>
        </>
    );
};

export { InputBaseField };
